import { useCallback, useEffect, useState } from 'react';
import { useDefinitionResource } from 'src/components/star-grid/hooks/useDefinitionResource';
import { loadStudioCatalog, loadStudioReport, loadStudioTemplate } from 'src/components/star-grid/services/reportStudioService';
import { scratchStudioTemplate } from 'src/components/star-grid/utils/studioScratchTemplate';
import { importedStudioTemplate, studioImportedReportId } from 'src/components/star-grid/utils/studioImportTemplate';

/** "Start from nothing" -- not a template id, so the template fetch is skipped for it. */
export const STUDIO_SCRATCH_ID = 'scratch';
/** A selection id naming an onboarded dataset: the from-scratch editor, on that dataset. */
export const STUDIO_DATASET_PREFIX = 'dataset:';

/** What the Studio is editing: a template (creates a report) or a saved report (replaces it). */
export type StudioSource =
  | { readonly kind: 'template' }
  | { readonly kind: 'report'; readonly reports: readonly { readonly id: number; readonly label: string }[] };
export const useStudioSelection = (source: StudioSource = { kind: 'template' }, copySuffix = '(copy)') => {
  const catalog = useDefinitionResource(loadStudioCatalog);
  const editingReport = source.kind === 'report';
  const [selectedId, setSelectedId] = useState('');
  const [dirty, setDirty] = useState(false);
  const [pendingId, setPendingId] = useState<string>();
  useEffect(() => {
    // Editing opens with no report chosen, so the user picks the one they mean rather than the first.
    if (selectedId || editingReport) return;
    // A template by default, NOT scratch: the Studio -- palette, shelves, layout preview -- only exists
    // for a template, so landing on scratch opened the bare form and the preview simply was not there.
    // Scratch stays selectable; it renders the from-scratch builder, which has no preview of its own.
    if (catalog.data?.templates.length) setSelectedId(catalog.data.templates.find((item) => item.ready)?.id ?? catalog.data.templates[0].id);
  }, [selectedId, catalog.data, editingReport, source]);
  const fetcher = useCallback(
    (signal: AbortSignal) => {
      // Scratch has no template to fetch; the from-scratch builder renders instead.
      if (!catalog.data || !selectedId) return Promise.resolve(undefined);
      // Scratch is the same editor a template opens, built locally with nothing selected -- there is
      // no definition to fetch for it.
      if (selectedId === STUDIO_SCRATCH_ID) return Promise.resolve(scratchStudioTemplate(catalog.data));
      if (selectedId.startsWith(STUDIO_DATASET_PREFIX))
        return Promise.resolve(scratchStudioTemplate(catalog.data, selectedId.slice(STUDIO_DATASET_PREFIX.length)));
      // "Import existing Report": the saved report's own definition, stripped of its report_id so the
      // save creates a copy instead of overwriting what was copied from.
      const imported = studioImportedReportId(selectedId);
      if (imported !== undefined)
        return loadStudioReport(imported, catalog.data, signal).then((loaded) => importedStudioTemplate(loaded, copySuffix));
      // Same editor either way; only where the definition comes from differs.
      return editingReport ? loadStudioReport(Number(selectedId), catalog.data, signal) : loadStudioTemplate(selectedId, catalog.data, signal);
    },
    [catalog.data, selectedId, editingReport, copySuffix]
  );
  const template = useDefinitionResource(fetcher);
  const choose = (id: string) => {
    if (id !== selectedId) {
      if (dirty) setPendingId(id);
      else setSelectedId(id);
    }
  };
  return {
    catalog,
    editingReport,
    template,
    selectedId,
    dirty,
    setDirty,
    choose,
    pendingId,
    confirm: () => {
      if (pendingId) {
        setSelectedId(pendingId);
        setPendingId(undefined);
        setDirty(false);
      }
    },
    cancel: () => setPendingId(undefined)
  };
};
